/**
 * Size Guide Drawer
 *
 * Themeunabhängig: kein Import aus Theme-Assets, keine Annahme über die
 * Markup-Struktur des Variantenwählers. Der Drawer selbst ist ein natives
 * <dialog> (Fokus-Falle, Escape und Backdrop kommen vom Browser).
 *
 *  - CM/INCH-Umschalter (Inch wird aus `data-cm` gerechnet, nichts doppelt gepflegt)
 *  - Klick auf eine Tabellenzeile wählt die passende Größe im echten Variantenwähler
 *  - Der Button unten schließt den Drawer und löst den echten Add-to-Cart aus
 *
 * Der Variantenwähler wird darüber gefunden, welches Formularelement Werte
 * anbietet, die zu den Zeilen der Tabelle passen — das funktioniert bei
 * <select>, Radio-Buttons und in jeder Sprache.
 */

const CM_TO_INCH = 0.3937007874;

/** @param {string | null | undefined} value */
const normalize = (value) => (value ?? '').trim().toLowerCase();

class SizeGuideDrawer extends HTMLElement {
  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {number} */
  #syncTimer = 0;

  connectedCallback() {
    const { signal } = this.#controller;

    /** @type {HTMLDialogElement | null} */
    this.dialog = this.querySelector('.size-guide__dialog');
    /** @type {HTMLButtonElement | null} */
    this.trigger = this.querySelector('.size-guide__trigger');
    /** @type {HTMLTableElement | null} */
    this.table = this.querySelector('.size-guide__table');
    /** @type {HTMLTableRowElement[]} */
    this.rows = Array.from(this.querySelectorAll('tr[data-size]'));
    /** @type {HTMLButtonElement | null} */
    this.submit = this.querySelector('.size-guide__submit');

    if (!this.dialog) return;

    this.trigger?.addEventListener('click', () => this.open(), { signal });
    this.querySelector('.size-guide__close')?.addEventListener('click', () => this.close(), { signal });

    // Klick auf den Backdrop trifft das <dialog> selbst.
    this.dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog) this.close();
      },
      { signal }
    );

    // Escape: abfangen, damit der Drawer herausfährt statt zu verschwinden.
    this.dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.close();
      },
      { signal }
    );

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onUnitChange, { signal });

    // Nur auf Geräten mit echtem Zeiger. Auf Touch-Geräten würde `pointerover`
    // beim ersten Tippen feuern und den Tap wie einen Hover wirken lassen —
    // der eigentliche Klick käme erst beim zweiten Tippen an.
    if (window.matchMedia('(hover: hover)').matches) {
      this.table?.addEventListener('pointerover', this.#onPointerOver, { signal });
      this.table?.addEventListener('pointerleave', this.#clearHover, { signal });
    }

    // Auswahl im echten Variantenwähler spiegeln.
    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (target instanceof Element && target !== this && !this.contains(target)) this.#scheduleSync();
      },
      { signal }
    );

    this.#sync();
  }

  disconnectedCallback() {
    this.#controller.abort();
    clearTimeout(this.#syncTimer);
  }

  open() {
    if (!this.dialog || this.dialog.open) return;

    this.dialog.showModal();
    // Erst im nächsten Frame öffnen, damit die Transition greift.
    requestAnimationFrame(() => this.dialog?.classList.add('is-open'));
    this.#sync();
  }

  close() {
    const dialog = this.dialog;
    if (!dialog?.open) return;

    dialog.classList.remove('is-open');

    let closed = false;
    const finish = () => {
      if (closed) return;
      closed = true;
      dialog.close();
    };

    dialog.addEventListener('transitionend', finish, { once: true });
    // Fallback, falls die Transition nicht feuert (reduzierte Bewegung o.ä.).
    setTimeout(finish, 400);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('.size-guide__submit')) {
      this.#addToCart();
      return;
    }

    const row = target.closest('tr[data-size]');
    if (row instanceof HTMLTableRowElement) this.#select(row.dataset.size ?? '');
  };

  /** @param {Event} event */
  #onUnitChange = (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.classList.contains('size-guide__unit-input')) {
      this.#setUnit(input.value);
    }
  };

  /**
   * Hebt Zeile und Spalte der überfahrenen Zelle hervor (PESO-Verhalten).
   * @param {PointerEvent} event
   */
  #onPointerOver = (event) => {
    const target = event.target;
    const cell = target instanceof Element ? target.closest('td, th') : null;

    this.#clearHover();
    if (!cell?.parentElement || !this.table) return;

    const index = Array.from(cell.parentElement.children).indexOf(cell);

    for (const row of this.table.rows) row.children[index]?.classList.add('is-hover');
    for (const sibling of cell.parentElement.children) sibling.classList.add('is-hover');
  };

  #clearHover = () => {
    for (const cell of this.querySelectorAll('.is-hover')) cell.classList.remove('is-hover');
  };

  /**
   * Schreibt alle Messwerte in der gewünschten Einheit. Nicht-numerische
   * Werte (z.B. "one size") bleiben unverändert.
   * @param {string} unit - 'cm' oder 'in'
   */
  #setUnit(unit) {
    for (const cell of this.querySelectorAll('[data-cm]')) {
      const raw = cell.getAttribute('data-cm') ?? '';

      if (unit === 'cm') {
        cell.textContent = raw;
        continue;
      }

      const value = Number.parseFloat(raw.replace(',', '.'));
      cell.textContent = Number.isNaN(value) ? raw : (value * CM_TO_INCH).toFixed(1);
    }

    this.dataset.unit = unit;
  }

  /**
   * Suchbereich für Variantenwähler und Add-to-Cart-Button: bevorzugt die
   * eigene Section, sonst das ganze Dokument.
   * @returns {ParentNode}
   */
  get #scope() {
    return this.closest('.shopify-section') ?? document;
  }

  /** @returns {HTMLFormElement | null} Das Add-to-Cart-Formular der Produktseite. */
  get #form() {
    return (
      this.#scope.querySelector('form[action*="/cart/add"]') ??
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  /**
   * Sucht das Bedienelement, dessen Werte zu den Tabellenzeilen passen — egal
   * ob <select> oder Radio-Gruppe, egal wie die Option heißt.
   *
   * Bewusst nicht auf das Cart-Formular eingegrenzt: viele Themes (u.a.
   * Horizon) rendern den Variantenwähler in einem eigenen Formular ohne
   * `action`, getrennt vom Formular mit dem Add-to-Cart-Button.
   *
   * @returns {{ select: HTMLSelectElement } | { radios: HTMLInputElement[] } | null}
   */
  #findControl() {
    const scope = this.#scope;

    for (const element of scope.querySelectorAll('select')) {
      const select = /** @type {HTMLSelectElement} */ (element);
      if (this.contains(select)) continue;

      const values = Array.from(select.options).map((option) => option.value);
      if (this.#matchesRows(values)) return { select };
    }

    /** @type {Map<string, HTMLInputElement[]>} */
    const groups = new Map();
    for (const element of scope.querySelectorAll('input[type="radio"]')) {
      const input = /** @type {HTMLInputElement} */ (element);
      if (this.contains(input)) continue;

      const group = groups.get(input.name) ?? [];
      group.push(input);
      groups.set(input.name, group);
    }

    for (const radios of groups.values()) {
      if (this.#matchesRows(radios.map((radio) => radio.value))) return { radios };
    }

    return null;
  }

  /**
   * Trifft mindestens die Hälfte der angebotenen Werte auf eine Tabellenzeile?
   * @param {string[]} values
   */
  #matchesRows(values) {
    if (values.length === 0) return false;

    const hits = values.filter((value) => this.#hasRow(value)).length;
    return hits > 0 && hits >= values.length / 2;
  }

  /** @param {string} size */
  #hasRow(size) {
    return this.rows.some((row) => normalize(row.dataset.size) === normalize(size));
  }

  /**
   * Wählt eine Größe im echten Variantenwähler. Das `change`-Ereignis übernimmt
   * das Theme — Preis, Verfügbarkeit und Button aktualisieren sich wie bei
   * einem normalen Klick.
   * @param {string} size
   */
  #select(size) {
    const control = this.#findControl();
    if (!control) return;

    if ('select' in control) {
      const option = Array.from(control.select.options).find((item) => normalize(item.value) === normalize(size));
      if (!option || control.select.value === option.value) return this.#sync(size);

      control.select.value = option.value;
      control.select.dispatchEvent(new Event('input', { bubbles: true }));
      control.select.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      const radio = control.radios.find((item) => normalize(item.value) === normalize(size));
      if (!radio || radio.checked) return this.#sync(size);

      radio.checked = true;
      radio.dispatchEvent(new Event('input', { bubbles: true }));
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.#sync(size);
  }

  /** @returns {string | null} Die aktuell gewählte Größe. */
  #currentSize() {
    const control = this.#findControl();
    if (!control) return null;

    if ('select' in control) return this.#hasRow(control.select.value) ? control.select.value : null;

    return control.radios.find((radio) => radio.checked)?.value ?? null;
  }

  #scheduleSync() {
    clearTimeout(this.#syncTimer);
    this.#syncTimer = window.setTimeout(() => this.#sync(), 50);
  }

  /**
   * Spiegelt die Auswahl in die Tabelle und schaltet den Button.
   * @param {string} [preferred] - Gerade angeklickte Größe; der Variantenwähler
   *   aktualisiert sich unter Umständen asynchron, deshalb hat der Klick Vorrang.
   */
  #sync(preferred) {
    const current = preferred ?? this.#currentSize();
    let hasSelection = false;
    let selectedAvailable = false;

    for (const row of this.rows) {
      const isSelected = Boolean(current) && normalize(row.dataset.size) === normalize(current);

      row.classList.toggle('is-selected', isSelected);
      row.querySelector('.size-guide__size-button')?.setAttribute('aria-pressed', String(isSelected));

      if (isSelected) {
        hasSelection = true;
        selectedAvailable = row.dataset.unavailable !== 'true';
      }
    }

    if (!this.submit) return;

    const { labelSelect = '', labelAdd = '', labelSoldOut = '' } = this.submit.dataset;

    this.submit.disabled = !hasSelection || !selectedAvailable;
    this.submit.textContent = !hasSelection ? labelSelect : selectedAvailable ? labelAdd : labelSoldOut;
  }

  /** Schließt den Drawer und löst den echten Add-to-Cart aus. */
  #addToCart() {
    const form = this.#form;
    const button = /** @type {HTMLElement | null} */ (
      form?.querySelector('[name="add"], [type="submit"], .product-form__submit')
    );

    this.close();
    button?.click();
  }
}

if (!customElements.get('size-guide-drawer')) {
  customElements.define('size-guide-drawer', SizeGuideDrawer);
}